import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  Alert, Button, Card, Empty, Form, Input, InputNumber, Modal, Select, Skeleton, Space, Statistic,
  Table, Tabs, Tag, Typography, message,
} from 'antd';
import type {
  DecideLeaveRequest, LeaveBalanceRow, LeaveRequestRow, LeaveStatus, LeaveType, StaffRow,
} from '@velora/contracts';
import { ApiError, productApi } from '../../../api';

/**
 * Admin — Staff leave.
 *   Requests tab: pending queue + full history + approve/reject modal.
 *   Balances tab: per-staff allocation editor for the current session.
 * Balances used = sum of approved requests within session window (derived,
 * so a status flip is enough to move the ledger — no double-book risk).
 */

const TYPES: LeaveType[] = ['casual', 'sick', 'earned', 'unpaid', 'other'];
const TYPE_TONE: Record<LeaveType, string> = {
  casual: 'blue', sick: 'volcano', earned: 'green', unpaid: 'default', other: 'purple',
};
const STATUS_TONE: Record<LeaveStatus, string> = {
  pending: 'processing', approved: 'success', rejected: 'error', cancelled: 'default',
};

export function LeavePage() {
  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>Staff leave</Typography.Title>
        <Typography.Text type="secondary" style={{ fontSize: 12.5 }}>
          Approve requests and set per-session allocations. Used-days come from approved requests, so the ledger stays honest without back-writes.
        </Typography.Text>
      </div>
      <Tabs
        defaultActiveKey="requests"
        items={[
          { key: 'requests', label: 'Requests', children: <RequestsTab /> },
          { key: 'balances', label: 'Balances', children: <BalancesTab /> },
        ]}
      />
    </Space>
  );
}

// ---------- Requests tab ----------

function RequestsTab() {
  const { institution = '' } = useParams<{ institution: string }>();
  const [filter, setFilter] = useState<'pending' | 'all'>('pending');
  const q = useQuery({
    queryKey: ['leave-requests', institution, filter],
    queryFn: () => productApi.listLeaveRequests(institution, filter === 'pending' ? { status: 'pending' } : {}),
    enabled: !!institution,
  });
  const [decideFor, setDecideFor] = useState<LeaveRequestRow | null>(null);

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 4 }} />;
  const rows = q.data?.rows ?? [];
  const pending = q.data?.pending_count ?? 0;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
        <Card size="small" style={{ minWidth: 160 }}>
          <Statistic title="Pending" value={pending} />
        </Card>
        <Card size="small" style={{ minWidth: 160 }}>
          <Statistic title="Total" value={q.data?.total ?? 0} />
        </Card>
        <div style={{ flex: 1 }} />
        <Select
          value={filter} onChange={setFilter} style={{ width: 160 }}
          options={[{ value: 'pending', label: 'Pending only' }, { value: 'all', label: 'All requests' }]}
        />
      </div>

      {rows.length === 0 ? (
        <Empty description={filter === 'pending' ? 'No pending requests.' : 'No requests yet.'} style={{ padding: 20 }} />
      ) : (
        <Table<LeaveRequestRow>
          size="small"
          rowKey="id"
          dataSource={rows}
          pagination={false}
          columns={[
            { title: 'Staff', dataIndex: 'staff_name', render: (v, r) => (
              <div>
                <div style={{ fontWeight: 600 }}>{v}</div>
                <div style={{ color: '#8a94a6', fontSize: 11 }}>{r.staff_role.replace(/_/g, ' ')}</div>
              </div>
            ) },
            { title: 'Type', dataIndex: 'leave_type', render: (v: LeaveType) => <Tag color={TYPE_TONE[v]}>{v}</Tag> },
            { title: 'Dates', render: (_: unknown, r) => (
              <span>
                <b>{r.from_date}</b> → <b>{r.to_date}</b>
                {r.half_day && <Tag style={{ marginLeft: 6 }}>half-day</Tag>}
              </span>
            ) },
            { title: 'Days', dataIndex: 'days', width: 70, align: 'right' },
            { title: 'Reason', dataIndex: 'reason', ellipsis: true },
            { title: 'Status', dataIndex: 'status', width: 110, render: (v: LeaveStatus) => <Tag color={STATUS_TONE[v]}>{v}</Tag> },
            { title: '', width: 140, render: (_: unknown, r) => r.status === 'pending' ? (
              <Button size="small" type="primary" onClick={() => setDecideFor(r)}>Review</Button>
            ) : (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {r.decided_by ? `by ${r.decided_by.split('@')[0]}` : '—'}
              </Typography.Text>
            ) },
          ]}
        />
      )}

      <DecideModal target={decideFor} institution={institution} onClose={() => setDecideFor(null)} />
    </Space>
  );
}

function DecideModal({
  target, institution, onClose,
}: { target: LeaveRequestRow | null; institution: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [form] = Form.useForm<{ note: string }>();
  const decide = useMutation({
    mutationFn: (input: DecideLeaveRequest) => productApi.decideLeaveRequest(institution, target!.id, input),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['leave-requests', institution] });
      void message.success(vars.decision === 'approve' ? 'Approved' : 'Rejected');
      onClose();
    },
  });
  const body = decide.error instanceof ApiError ? (decide.error.body as { message?: string } | null) : null;
  const err = body?.message ?? (decide.error instanceof Error ? decide.error.message : null);

  return (
    <Modal
      open={!!target}
      title={target ? `Leave — ${target.staff_name}` : ''}
      onCancel={() => { decide.reset(); onClose(); }}
      footer={null}
      destroyOnClose
    >
      {target && (
        <Form form={form} layout="vertical" preserve={false}>
          <div style={{ background: '#f7f7f5', border: '1px solid #eef0f3', padding: 12, borderRadius: 6, marginBottom: 12 }}>
            <div style={{ marginBottom: 4 }}>
              <Tag color={TYPE_TONE[target.leave_type]}>{target.leave_type}</Tag>
              <b>{target.days} day{target.days === 1 ? '' : 's'}</b> · {target.from_date} → {target.to_date}
              {target.half_day && <Tag style={{ marginLeft: 6 }}>half-day</Tag>}
            </div>
            <div style={{ fontSize: 12.5, color: '#3f4a63' }}>{target.reason}</div>
          </div>
          <Form.Item name="note" label="Decision note (optional)">
            <Input.TextArea rows={3} placeholder="Shown to the staff member." />
          </Form.Item>
          {err && <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} />}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button
              danger
              loading={decide.isPending && form.getFieldValue('note') !== undefined && decide.variables?.decision === 'reject'}
              onClick={() => decide.mutate({ decision: 'reject', note: form.getFieldValue('note') || undefined })}
            >
              Reject
            </Button>
            <Button
              type="primary"
              loading={decide.isPending && decide.variables?.decision === 'approve'}
              onClick={() => decide.mutate({ decision: 'approve', note: form.getFieldValue('note') || undefined })}
            >
              Approve
            </Button>
          </div>
        </Form>
      )}
    </Modal>
  );
}

// ---------- Balances tab ----------

function BalancesTab() {
  const { institution = '' } = useParams<{ institution: string }>();
  const staffQ = useQuery({
    queryKey: ['staff-list', institution],
    queryFn: () => productApi.listStaff(institution),
    enabled: !!institution,
  });
  const active = useMemo(
    () => (staffQ.data?.rows ?? []).filter((s: StaffRow) => s.status === 'active'),
    [staffQ.data],
  );
  const [staffId, setStaffId] = useState<string | undefined>();
  useMemo(() => {
    if (!staffId && active.length > 0) setStaffId(active[0]!.id);
  }, [active, staffId]);

  if (staffQ.isLoading) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (active.length === 0) {
    return <Alert type="info" showIcon message="No active staff yet — add staff before setting leave allocations." />;
  }

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Typography.Text strong>Staff:</Typography.Text>
        <Select
          value={staffId}
          onChange={setStaffId}
          style={{ minWidth: 260 }}
          options={active.map((s: StaffRow) => ({
            value: s.id, label: `${s.name} · ${s.role.replace(/_/g, ' ')}`,
          }))}
          showSearch
          optionFilterProp="label"
        />
      </div>
      {staffId && <BalanceEditor institution={institution} staffId={staffId} />}
    </Space>
  );
}

function BalanceEditor({ institution, staffId }: { institution: string; staffId: string }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['leave-balances', institution, staffId],
    queryFn: () => productApi.getLeaveBalances(institution, staffId),
    enabled: !!institution && !!staffId,
  });
  const [draft, setDraft] = useState<Record<LeaveType, number>>();

  const balances: LeaveBalanceRow[] = q.data?.balances ?? [];
  const sessionId = q.data?.session_id ?? null;
  const sessionLabel = q.data?.session_label ?? null;

  const current: Record<LeaveType, number> = useMemo(() => {
    const m = {} as Record<LeaveType, number>;
    for (const t of TYPES) m[t] = balances.find((b) => b.leave_type === t)?.allocated_days ?? 0;
    return m;
  }, [balances]);

  const effective = draft ?? current;

  const save = useMutation({
    mutationFn: () =>
      productApi.setLeaveBalances(institution, {
        staff_id: staffId,
        session_id: sessionId!,
        entries: TYPES.map((t) => ({ leave_type: t, allocated_days: effective[t] })),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leave-balances', institution, staffId] });
      setDraft(undefined);
      void message.success('Saved');
    },
  });

  if (q.isLoading) return <Skeleton active paragraph={{ rows: 3 }} />;
  if (!sessionId) {
    return <Alert type="warning" showIcon message="No current academic session — set one in Setup → Calendar first." />;
  }
  const err = save.error instanceof Error ? save.error.message : null;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Session · <b>{sessionLabel}</b>
      </Typography.Text>
      <Table<{ leave_type: LeaveType; allocated: number; used: number; remaining: number }>
        size="small"
        rowKey="leave_type"
        pagination={false}
        dataSource={TYPES.map((t) => {
          const b = balances.find((x) => x.leave_type === t);
          return {
            leave_type: t,
            allocated: effective[t],
            used: b?.used_days ?? 0,
            remaining: effective[t] - (b?.used_days ?? 0),
          };
        })}
        columns={[
          { title: 'Type', dataIndex: 'leave_type', render: (v: LeaveType) => <Tag color={TYPE_TONE[v]}>{v}</Tag> },
          { title: 'Allocated', dataIndex: 'allocated', width: 180, render: (v: number, r) => (
            <InputNumber
              min={0} max={365} step={0.5}
              value={v}
              onChange={(n) => setDraft((prev) => ({ ...(prev ?? current), [r.leave_type]: n ?? 0 }))}
            />
          ) },
          { title: 'Used', dataIndex: 'used', width: 100, align: 'right' },
          { title: 'Remaining', dataIndex: 'remaining', width: 120, align: 'right',
            render: (v: number) => <b style={{ color: v < 0 ? '#b3382f' : '#14213d' }}>{v}</b> },
        ]}
      />
      {err && <Alert type="error" showIcon message={err} />}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={() => setDraft(undefined)} disabled={!draft}>Reset</Button>
        <Button type="primary" onClick={() => save.mutate()} loading={save.isPending} disabled={!draft}>Save</Button>
      </div>
    </Space>
  );
}
